/**
 * 超级管理员配置（通过API动态获取）
 *
 * 该模块通过后端API实时检查用户的超级管理员权限
 * 权限状态在应用启动时加载并缓存，可通过 refreshSuperAdminStatus 手动刷新
 *
 * 使用方法：
 * 1. 应用启动时调用 initSuperAdminStatus() 初始化权限状态
 * 2. 组件中通过 isSuperAdmin() 同步读取权限状态
 * 3. 需要刷新时调用 refreshSuperAdminStatus()
 */

import { ref } from 'vue'
import psHttp from '@patsnap/common-ps-http'
import { DRAFTING_URI } from '@/constants/base-url'

/**
 * 超级管理员权限状态
 * - null: 未初始化
 * - true: 是超级管理员
 * - false: 不是超级管理员
 */
const isSuperAdminStatus = ref<boolean | null>(null)

/**
 * 权限加载状态
 */
const isLoading = ref(false)

/**
 * API响应类型
 */
interface CheckSuperAdminResponse {
  status: boolean
  message: string
  data: {
    is_super_admin: boolean
  }
  error_code: number | null
  error: string | null
}

/**
 * 从后端API检查超级管理员权限
 *
 * @returns Promise<boolean> 是否是超级管理员
 */
async function checkSuperAdminFromAPI(): Promise<boolean> {
  try {
    const response = await psHttp<CheckSuperAdminResponse>({
      url: `${DRAFTING_URI}/api/user/settings/check_super_admin`,
      method: 'GET',
      transformCode: true,
      headers: {
        'x-api-version': '1.0',
      },
    })

    if (response.data?.status && response.data?.data) {
      return response.data.data.is_super_admin
    }

    return false
  } catch (error) {
    console.error('[Admin] Failed to check super admin permission:', error)
    return false
  }
}

/**
 * 初始化超级管理员权限状态
 * 应在应用启动时调用此方法
 *
 * @returns Promise<void>
 */
export async function initSuperAdminStatus(): Promise<void> {
  if (isLoading.value) {
    return
  }

  isLoading.value = true

  try {
    const result = await checkSuperAdminFromAPI()
    isSuperAdminStatus.value = result

    console.log('[Admin] Super admin permission initialized successfully:', result)
  } catch (error) {
    console.error('[Admin] Failed to initialize super admin permission:', error)
    isSuperAdminStatus.value = false
  } finally {
    isLoading.value = false
  }
}

/**
 * 刷新超级管理员权限状态
 * 在需要重新检查权限时调用
 *
 * @returns Promise<boolean> 是否是超级管理员
 */
export async function refreshSuperAdminStatus(): Promise<boolean> {
  isLoading.value = true

  try {
    const result = await checkSuperAdminFromAPI()
    isSuperAdminStatus.value = result

    console.log('[Admin] Super admin permission refreshed successfully:', result)
    return result
  } catch (error) {
    console.error('[Admin] Failed to refresh super admin permission:', error)
    isSuperAdminStatus.value = false
    return false
  } finally {
    isLoading.value = false
  }
}

/**
 * 判断当前用户是否是超级管理员（同步方法）
 *
 * 注意：此方法返回缓存的权限状态，在使用前需确保已调用 initSuperAdminStatus()
 * 如果权限状态未初始化（null），默认返回 false
 *
 * @returns boolean 是否是超级管理员
 */
export function isSuperAdmin(): boolean {
  // 如果状态未初始化，返回 false（保守策略）
  if (isSuperAdminStatus.value === null) {
    console.warn(
      '[Admin] Super admin permission status is not initialized. Please ensure initSuperAdminStatus() is called during app startup.'
    )
    return false
  }

  return isSuperAdminStatus.value
}

/**
 * 获取权限加载状态
 *
 * @returns boolean 是否正在加载
 */
export function isPermissionLoading(): boolean {
  return isLoading.value
}

/**
 * 获取权限状态（响应式）
 * 用于组件中需要响应式更新的场景
 *
 * @returns Ref<boolean | null>
 */
export function getSuperAdminStatus() {
  return isSuperAdminStatus
}
